import type { Building } from "building-types/building/buildingTypes";
import type { ComputedColumnPlacement } from "building-types/columns/columnBaseTypes";
import type { StructuralColumnDescriptor } from "building-types/columns/columnDescriptorTypes";
import type { GeneratedColumn } from "building-types/columns/columnGeneratedTypes";
import type { Column } from "building-types/columns/columnTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { ColumnHandle } from "building-types/handles/docEntityHandleTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { docEntityGetChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { getStructuralColumnTable } from "doc/utils/columnDescriptorUtils";
import type { PolyLine2 } from "geom/polyLine2Types";
import { calculateVerticalDatumZ, isRelativeVerticalDatum } from "../elements/columns/columnDatumUtils";
import { resolveColumnPlacementV1, resolveHorizontalDatumTable } from "../elements/columns/columnDeprecatedUtils";
import {
  computeStructuralColumnSections,
  generateColumnsAtComputedPlacements,
  generateSectionBoundaryArray,
  resolveColumnPlacementV2,
  resolveRelativeVerticalDatumForTopAndBottom,
} from "../elements/columns/columnUtils";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  levels,
  settings,
  resolvedDatums,
  walls,
  rooms,
  roomIndex,
  wallMasses,
  mirror,
  doc: { columns, optionSets, instanceOptionSets },
  optionChoices,
  roomOutlineTable,
  entityDisabledByGroup,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    levels,
    columnSettings: settings.column,
    resolvedDatums,
    walls,
    rooms,
    roomIndex,
    wallMasses,
    mirror,
    optionChoices: resolveOptionsInFormula(columns, definitions, optionChoices),
    rawOptionChoices: optionChoices,
    definitions,
    columns,
    roomOutlineTable,
    entityDisabledByGroup,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  optionChoices: "shallow",
});

/**
 * Evaluate a column entity's formulas using either component-instance-aware
 * option resolution (when the room belongs to a component instance) or the
 * archetype option choices (with group disabling) for non-component rooms.
 *
 * Mirrors the per-instance evaluation pattern used by railings/casework/props
 * so that an instance option set can independently toggle a column's
 * `disabled` field for one component instance without affecting siblings.
 */
function evaluateColumnForRoom(
  rawColumn: Column,
  componentInstanceChain: readonly string[] | undefined,
  definitions: OptionDefinitions,
  rawOptionChoices: OptionChoices,
  optionChoices: OptionChoices,
  entityDisabledByGroup: Table<boolean>,
): Column {
  if (componentInstanceChain && componentInstanceChain.length > 0) {
    return evaluateEntityForComponentInstance(rawColumn, componentInstanceChain, definitions, rawOptionChoices);
  }
  return evalFormulas(rawColumn, (id) => optionChoices[id], undefined, entityDisabledByGroup);
}

function generate({
  levels,
  columnSettings,
  resolvedDatums,
  walls,
  rooms,
  roomIndex,
  wallMasses,
  mirror,
  optionChoices,
  rawOptionChoices,
  definitions,
  columns: docColumns,
  roomOutlineTable,
  entityDisabledByGroup,
}: ReturnType<typeof select>): {
  columns: Table<GeneratedColumn>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];
  const columns: RWTable<GeneratedColumn> = {};
  const cachedHorizontalDatumTable: RWTable<PolyLine2> = {};
  const wallLayerExtentsCache: WallLayerExtentsCache = {};

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const baseRoomId = getSourceRoomId(room);
    const componentInstanceChain = room.componentTransform?.componentInstanceChain;
    const roomColumns = docEntityGetChildrenFromTable(docColumns, baseRoomId);

    for (const columnId in roomColumns) {
      const handle: ColumnHandle = {
        type: "COLUMN",
        id: columnId,
      };

      const rawColumnSet = roomColumns[columnId];
      const roomColumnSet = evaluateColumnForRoom(
        rawColumnSet,
        componentInstanceChain,
        definitions,
        rawOptionChoices,
        optionChoices,
        entityDisabledByGroup,
      );

      if (roomColumnSet.disabled) {
        continue;
      }

      const { catalogColumnId, edgeId, paletteId, verticalDatum, version } = roomColumnSet;

      const structuralColumn: StructuralColumnDescriptor = getStructuralColumnTable(columnSettings)[catalogColumnId];

      if (!structuralColumn) {
        issues.push({
          type: BuildingGenerationIssueType.COLUMN_GENERATION_ISSUE,
          levelId: room.level.id,
          handle,
          message: `Column properties for ${roomColumnSet.name} at Level: ${room.level.name} and Room: ${room.name} not found in the catalog`,
        });
        continue;
      }

      const computedColumnPlacementArray: ComputedColumnPlacement[] = [];

      const relativeDatums = isRelativeVerticalDatum(verticalDatum)
        ? resolveRelativeVerticalDatumForTopAndBottom(verticalDatum, levels, room)
        : {};

      const colSections = computeStructuralColumnSections(
        structuralColumn,
        resolvedDatums,
        relativeDatums,
        calculateVerticalDatumZ,
        verticalDatum,
      );

      if (colSections.length === 0) {
        issues.push({
          type: BuildingGenerationIssueType.COLUMN_GENERATION_ISSUE,
          levelId: room.level.id,
          handle,
          message: `Column section for ${roomColumnSet.name} at Level: ${room.level.name} and Room: ${room.name} cannot be computed`,
        });
        continue;
      }

      const sectionBoundaries = generateSectionBoundaryArray(colSections);

      if (sectionBoundaries.length === 0) {
        issues.push({
          type: BuildingGenerationIssueType.COLUMN_GENERATION_ISSUE,
          levelId: room.level.id,
          handle,
          message: `Column section boundary for ${roomColumnSet.name} at Level: ${room.level.name} and Room: ${room.name} cannot be computed`,
        });
        continue;
      }

      switch (version) {
        case "V1":
          // this is a bespoke means to find edge length and is replaced by wallLayerExtentsCache in V2
          resolveHorizontalDatumTable({ wallMasses, mirror, roomIndex }, room, edgeId, cachedHorizontalDatumTable);

          resolveColumnPlacementV1(
            walls,
            room,
            roomColumnSet,
            cachedHorizontalDatumTable,
            sectionBoundaries[0],
            mirror,
            computedColumnPlacementArray,
          );
          break;
        case "V2":
          // V2 enables move and copy commands, placement manips, etc.
          resolveColumnPlacementV2(
            room,
            roomColumnSet,
            mirror,
            computedColumnPlacementArray,
            roomIndex,
            wallLayerExtentsCache,
            wallMasses,
            roomOutlineTable[room.id],
            sectionBoundaries[0],
          );
          break;
      }

      if (computedColumnPlacementArray.length) {
        generateColumnsAtComputedPlacements(
          computedColumnPlacementArray,
          sectionBoundaries,
          edgeId,
          room,
          paletteId,
          mirror,
          columns,
          handle,
        );
      }
    }
  }

  return { columns, issues };
}

export default memo(select, generate, compare);
